import { useQueries, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import type { FunnelStepResponse } from '@troia-v3/types';

import { funnelsApi } from '@/modules/funnels';

import { leadsService } from '../services/leadsService';

export const useLeadsCampaignsDropdown = () => {
  return useQuery({
    queryKey: ['leads-campaigns-dropdown'],
    queryFn: () => leadsService.getCampaignNamesDropdown(),
    staleTime: 5 * 60 * 1000,
  });
};

export const useLeadsAdsetsDropdown = () => {
  return useQuery({
    queryKey: ['leads-adsets-dropdown'],
    queryFn: () => leadsService.getAdsetNamesDropdown(),
    staleTime: 5 * 60 * 1000,
  });
};

export const useLeadsAdsDropdown = () => {
  return useQuery({
    queryKey: ['leads-ads-dropdown'],
    queryFn: () => leadsService.getAdNamesDropdown(),
    staleTime: 5 * 60 * 1000,
  });
};

export const useLeadsOriginsDropdown = () => {
  return useQuery({
    queryKey: ['leads-origins-dropdown'],
    queryFn: () => leadsService.getOriginsDropdown(),
    staleTime: 5 * 60 * 1000,
  });
};

export const useLeadsTagsDropdown = () => {
  return useQuery({
    queryKey: ['leads-tags-dropdown'],
    queryFn: () => leadsService.getTagsDropdown(),
    staleTime: 5 * 60 * 1000,
  });
};

export const useLeadsTypesDropdown = () => {
  return useQuery({
    queryKey: ['leads-types-dropdown'],
    queryFn: () => leadsService.getTypesDropdown(),
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Quem pode ser escolhido como responsável de lead no funil (cadastro e
 * transferência): participantes do funil em qualquer papel; funil sem
 * participantes → todos os usuários ativos. Sem funil, a query não roda.
 */
export const useLeadAssigneesDropdown = (funnelId: string | undefined, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: ['leads-assignees-dropdown', funnelId],
    queryFn: () => leadsService.getAssigneesForFunnel(funnelId!),
    staleTime: 5 * 60 * 1000,
    enabled: (options?.enabled ?? true) && !!funnelId,
  });
};

/**
 * Operadores do filtro "por operador" com funil(is) escolhido(s): quem
 * participa do funil (qualquer papel) + quem tem lead atribuído nele, mesmo
 * desativado ou excluído. Sem funil, a query não roda.
 */
export const useLeadOperatorsDropdown = ({
  funnelIds,
  scope,
  enabled = true,
}: {
  funnelIds: string[];
  scope?: 'team' | 'all';
  enabled?: boolean;
}) => {
  const funnelKey = [...funnelIds].sort().join(',');
  return useQuery({
    queryKey: ['leads-operators-dropdown', funnelKey, scope ?? 'all'],
    queryFn: () => leadsService.getOperatorsForFunnels(funnelKey.split(','), scope),
    staleTime: 60 * 1000,
    enabled: enabled && funnelKey !== '',
  });
};

/**
 * Etapas dos funis selecionados, para o filtro "Etapa" da listagem.
 *
 * Uma query por funil, via `/funnel-steps/by-funnel/:funnelId` — a rota
 * cross-module SEM permissão de módulo (NUNCA #15). `GET /funnel-steps`
 * traria tudo de uma vez, mas exige `funnels:read`, que o operador de CRM
 * normalmente não tem: o filtro ficaria vazio para quem mais usa a tela.
 *
 * A `queryKey` é a MESMA de `useFunnelStepsDropdown`, então as etapas já
 * carregadas por outra tela (drawer de transferência, kanban) são reusadas do
 * cache em vez de rebuscadas.
 */
export const useFunnelStepsByFunnels = (funnelIds: string[]) => {
  const results = useQueries({
    queries: funnelIds.map((funnelId) => ({
      queryKey: ['funnel-steps-dropdown', funnelId],
      queryFn: () => funnelsApi.getStepsByFunnelId(funnelId),
      staleTime: 10 * 60 * 1000,
    })),
  });

  // Identidade estável: `useQueries` devolve um array novo a cada render, e
  // este mapa alimenta `useMemo`s de opções de filtro rio abaixo. Sem a
  // memoização, cada render refaria todas as listas de opções.
  const signature = results.map((result) => result.dataUpdatedAt).join('|');
  const stepsByFunnel = useMemo(() => {
    const map: Record<string, FunnelStepResponse[]> = {};
    funnelIds.forEach((funnelId, index) => {
      const data = results[index]?.data;
      if (data) map[funnelId] = data;
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `results` muda de
    // identidade a cada render; `signature` é o que de fato marca dado novo.
  }, [funnelIds.join('|'), signature]);

  return {
    stepsByFunnel,
    isLoading: results.some((result) => result.isLoading),
  };
};

/**
 * Lista com busca para escolher UM lead (nó "Salvar formulário" dos fluxos,
 * 2026-09-28). Só busca com o painel aberto (CLAUDE.md SEMPRE #10) — quem
 * chama aplica `useDebouncedValue` no termo.
 */
export const useLeadsPicker = (options: { enabled: boolean; search: string }) => {
  return useQuery({
    queryKey: ['leads', 'dropdown', options.search],
    queryFn: () => leadsService.getLeadsDropdown(options.search),
    enabled: options.enabled,
    staleTime: 60 * 1000,
  });
};

/** O lead já escolhido (rótulo). Lead apagado ou fora da visibilidade → sem rótulo. */
export const useLeadPickerItem = (leadId: string | undefined) => {
  return useQuery({
    queryKey: ['leads', 'dropdown', 'item', leadId],
    queryFn: () => leadsService.getLeadDropdownItem(leadId as string),
    enabled: !!leadId,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
};
